import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Loader2, Check, Pencil, ExternalLink, Plus } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useKaartToken, GRIJS } from "@/lib/kaartToken";
import OpenOpnieuw from "./OpenOpnieuw";
import { useTaal } from "@/contexts/TaalContext";

/** Een taak in TASQ, in twee stappen die niet samen te voegen zijn.
 *
 *  Post 10b-glas, en dit scherm draagt maatregel 1 van CONSTRAINTS 0.11:
 *  bevestiging op glas MET DE EXACTE VELDEN ERVOOR.
 *
 *  DRIE TOESTANDEN EN ZE ZIJN NIET INWISSELBAAR:
 *
 *    KIEZEN      De zin noemde geen werkruimte, of er pasten er meer. Er is
 *                NIETS opgeslagen; de lijst staat er als klikbare keuzes.
 *    NALEZEN     De vijf velden staan er ALLEEN-LEZEN, precies zoals ze zijn
 *                opgeslagen. Dit is het moment van de bevestiging.
 *    AANGEMAAKT  De taak staat in TASQ, met een link erheen.
 *
 *  DE VELDEN KOMEN VAN DE SERVER EN NIET UIT HET BERICHT. Het blok onder het
 *  antwoord draagt alleen een id; dit scherm haalt de rij op. Dat is geen omweg
 *  maar de hele bedoeling: wat hier te lezen staat is wat er is OPGESLAGEN, en
 *  dat is precies wat de uitvoerroute accepteert. Zou de kaart zijn velden uit
 *  de berichttekst lezen, dan bevestigt Frank wat een taalmodel heeft kunnen
 *  schrijven. Zelfde redenering als bij `MailAntwoord`.
 *
 *  ER WORDT NIETS AUTOMATISCH AANGEMAAKT. Geen model, geen agent en geen
 *  achtergrondtaak raakt deze knop; dit is een handeling van Frank.
 */
/** De naam die niet bestaat, uit de reden van de server.
 *
 *  **ALLEEN BIJ "geen werkruimte heet X", en dat is de hele zorg.** Bij "meer
 *  dan een werkruimte past op X" bestaat die naam juist wel en is er niets aan
 *  te maken; een knop daar zou een tweede werkruimte opleveren naast de twee
 *  die al passen.
 *
 *  **BIJ MEER DAN EEN ONBEKENDE NAAM KOMT ER GEEN KNOP.** "geen werkruimte heet
 *  X of Y" laat in het midden welke Frank bedoelde, en er een gokken is precies
 *  wat dit hele pad niet doet.
 */
export function uitReden(reden) {
  const tekst = reden || "";
  if (!tekst.startsWith("geen werkruimte heet ")) return "";
  const namen = tekst.match(/"([^"]+)"/g) || [];
  return namen.length === 1 ? namen[0].slice(1, -1) : "";
}

export default function TaakVoorstel({ voorstelId, keuze, zin, berichtId, onGekozen, onWijzig, onOpnieuw }) {
  const { t } = useTaal();
  const [rij, setRij] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState("");
  const [keuzes, setKeuzes] = useState(null);
  // **DE NAAM DIE NIET BESTAAT, UIT DE REDEN VAN DE SERVER.** Hij wordt daar
  // tussen aanhalingstekens gezet; die vorm is de afspraak tussen de twee
  // kanten en er staat een wachter op. Zelf de zin ontleden zou een tweede
  // lezing opleveren die met de eerste uit de pas kan lopen.
  const [onbekendeNaam, setOnbekendeNaam] = useState("");
  // WF-40 (post a): het token van deze kaart, zolang er iets te bevestigen is.
  const { token, vernieuw, vervallen, laatVervallen, reden } = useKaartToken(
    rij?.id ? `${API}/taken/${rij.id}/toon` : "", Boolean(rij?.id && !rij.aangemaakt));

  // DE VELDEN OPHALEN, want het blok draagt er geen. Bij een keuzevraag is er
  // geen rij en wordt de lijst opgehaald met dezelfde zin.
  useEffect(() => {
    let levend = true;
    const haal = async () => {
      try {
        if (keuze) {
          const r = await axios.post(`${API}/taken/voorstel`, { zin });
          if (levend) {
            if (!r.data.keuze_nodig && r.data.id) {
              // POST E: een zin uit een Fathom-actiepunt kan meteen een voorstel
              // opleveren (een werkruimte die past). Dan is er niets te kiezen
              // en hoort het voorstel op de kaart, niet een lege keuzelijst.
              setRij(r.data);
            } else {
              setKeuzes(r.data.werkruimtes || []);
              setOnbekendeNaam(uitReden(r.data.reden));
            }
          }
        } else {
          const r = await axios.get(`${API}/taken/${voorstelId}`);
          if (levend) setRij(r.data);
        }
      } catch (e) {
        if (levend) setFout(e.response?.data?.detail || t("Het voorstel is niet op te halen"));
      }
    };
    haal();
    return () => { levend = false; };
  }, [voorstelId, keuze, zin, t]);

  // **EEN KLIK IS DE KEUZE, PUNT. Franks eis van 12 september 2026, na de
  // eerste echte schrijfproef.**
  //
  // Hier stond `onOpnieuw(zin + ", werkruimte " + naam)`: de klik werd als
  // TEKST teruggestuurd en ging als nieuw chatbericht opnieuw door de
  // ontleding. Die pakt de EERSTE werkruimtefrase in de zin, en dat was de
  // naam die Frank oorspronkelijk had getypt. Hij klikte Direkto aan en kreeg
  // de lijst opnieuw, want "Groenprint" bestaat niet.
  //
  // **EEN KEUZE DIE ALS TEKST TERUGREIST, IS GEEN KEUZE MEER MAAR EEN NIEUW
  // VOORSTEL.** De naam gaat daarom als EIGEN VELD mee, langs de ontleding
  // heen, en het voorstel verschijnt meteen op deze kaart. Er komt geen tweede
  // chatronde meer aan te pas.
  const kies = async (naam, aanTeMaken = "") => {
    setBezig(true);
    setFout("");
    try {
      const r = await axios.post(`${API}/taken/voorstel`, {
        zin,
        ...(naam ? { werkruimte_naam: naam } : {}),
        ...(aanTeMaken ? { nieuwe_werkruimte: aanTeMaken } : {}),
        // POST 10c STAP 1: het bericht met de vraag, zodat de server de vraag
        // daar vervangt door het voorstel. Ook na herladen is de kop dan weg.
        ...(berichtId ? { bericht_id: berichtId } : {}),
      });
      if (r.data.keuze_nodig) {
        // KAN NOG STEEDS: de werkruimte is intussen weggehaald of hernoemd.
        setKeuzes(r.data.werkruimtes || []);
        setFout(r.data.reden || "");
      } else {
        setRij(r.data);
        // De kop gaat weg op het scherm, ook als de server het bericht niet kon
        // bijwerken: de vraag is hier beantwoord. Dan staat de vaste zin bij een
        // voorstel erboven, dezelfde die de server zou hebben geschreven.
        if (onGekozen && r.data.bericht_tekst) onGekozen(r.data.bericht_tekst);
      }
    } catch (e) {
      setFout(e.response?.data?.detail || t("Het voorstel is niet op te halen"));
    } finally {
      setBezig(false);
    }
  };

  const bevestig = async () => {
    setBezig(true);
    setFout("");
    try {
      // DE VIJF VELDEN GAAN MEE ZOALS ZE OP HET SCHERM STAAN. Hier iets anders
      // meesturen zou het hele onderscheid opheffen: dan bevestigt Frank een
      // nummer en geen taak.
      const r = await axios.post(`${API}/taken/${rij.id}/uitvoeren`, {
        titel: rij.titel,
        werkruimte_id: rij.werkruimte_id,
        werkruimte_naam: rij.werkruimte_naam,
        deadline_iso: rij.deadline_iso,
        prioriteit: rij.prioriteit,
        token,
      });
      setRij(r.data);
      toast.success(t("Taak aangemaakt in TASQ"));
    } catch (e) {
      const tekst = e.response?.data?.detail || t("Het aanmaken is niet gelukt");
      setFout(tekst);
      // FIX 4: het token is op of ongeldig. GEEN stil nieuw token; alleen
      // "Open opnieuw" haalt er een. Punt 5: de reden blijft bewaard na herladen.
      laatVervallen(tekst);
    } finally {
      setBezig(false);
    }
  };

  // FIX 4: een verse kaart: de rij opnieuw van de server, dan een nieuw token.
  // Is zij intussen elders aangemaakt, dan toont de kaart dat en blijft het
  // token weg (`actief` is dan onwaar).
  const openOpnieuw = async () => {
    setBezig(true);
    setFout("");
    try {
      const r = await axios.get(`${API}/taken/${rij.id}`);
      setRij(r.data);
      if (!r.data.aangemaakt) await vernieuw();
    } catch (e) {
      setFout(e.response?.data?.detail || t("Het voorstel is niet op te halen"));
    } finally {
      setBezig(false);
    }
  };

  // WIJZIGEN ZET DE VELDEN TERUG ALS ZIN, zodat Frank hem aanpast en opnieuw
  // stuurt. Franks eis van 12 september 2026. Er wordt NIETS gewijzigd aan de
  // opgeslagen rij: die blijft staan als voorstel dat niet is doorgegaan, en
  // dat is precies wat het log hoort te tonen.
  const wijzig = () => {
    const stukken = [`zet een taak: ${rij.titel}`];
    if (rij.deadline_iso) stukken.push(rij.deadline_op_glas.replace(/, hele dag$/, ""));
    stukken.push(`werkruimte ${rij.werkruimte_naam}`);
    onWijzig?.(stukken.join(", "));
  };

  if (fout && !rij && !keuzes) {
    return (
      <div className="border rounded-lg p-3" data-testid="taak-fout">
        <p className="text-xs text-[hsl(var(--muted-foreground))]">{fout}</p>
      </div>
    );
  }

  if (keuze && !rij) {
    return (
      <div className="border rounded-lg p-3 space-y-2" data-testid="taak-keuze">
        <p className="text-xs text-[hsl(var(--muted-foreground))]">
          {t("Er is nog niets opgeslagen. Kies de werkruimte.")}
        </p>
        {/* **BOVENAAN: DE WERKRUIMTE DIE ER NOG NIET IS. Post 10b-fix punt 5,
            Franks besluit van 12 september 2026.** NovaMund mag er een
            aanmaken, alleen hiervandaan en alleen na bevestiging.

            HIJ STAAT ER ALLEEN BIJ EEN NAAM DIE NIET BESTAAT. Wie er geen
            noemde, krijgt hem niet: dan is er niets om aan te maken en zou de
            knop vragen om een naam die nergens vandaan komt. */}
        {onbekendeNaam && (
          <button
            type="button"
            className="w-full text-left text-sm px-3 py-3 min-h-11 rounded border border-dashed hover:bg-[hsl(var(--muted))]"
            data-testid="taak-maak-werkruimte-knop"
            disabled={bezig}
            onClick={() => kies(null, onbekendeNaam)}
          >
            <Plus className="h-3 w-3 mr-1 inline" />
            {t("Werkruimte")} <span className="font-medium">{onbekendeNaam}</span> {t("aanmaken")}
          </button>
        )}
        {keuzes === null ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <ul className="space-y-1">
            {keuzes.map((w) => (
              <li key={w.id}>
                {/* MINSTENS 44 PUNTEN HOOG, want alles daaronder is op een
                    telefoon een defect en geen detail (CONSTRAINTS 0.3b). */}
                <button
                  type="button"
                  className="w-full text-left text-sm px-3 py-3 min-h-11 rounded border hover:bg-[hsl(var(--muted))]"
                  data-testid="taak-keuze-knop"
                  disabled={bezig}
                  onClick={() => kies(w.naam)}
                >
                  <span className="font-medium">{w.naam}</span>
                  <span className="ml-2 text-xs text-[hsl(var(--muted-foreground))]">
                    {t("{n} open", { n: w.open })}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  if (!rij) {
    return <Loader2 className="h-4 w-4 animate-spin" data-testid="taak-laadt" />;
  }

  if (rij.aangemaakt) {
    return (
      <div className="border rounded-lg p-3 space-y-1" data-testid="taak-aangemaakt">
        <p className="text-xs font-medium">{t("Aangemaakt in {naam}", { naam: rij.werkruimte_naam })}</p>
        {/* **DE DEADLINE STAAT OOK HIER. Franks eis van 12 september 2026, na
            de eerste echte schrijfproef.**

            Hij stond alleen op de VOORSTELkaart. Wie na het aanmaken wil weten
            wanneer de taak afmoet, moest naar TASQ - terwijl dat precies het
            veld is waar een fout in kan sluipen en waar een mens dus naar
            kijkt. De waarde komt uit dezelfde `deadline_op_glas` die de
            voorstelkaart toont, dus de twee kunnen niet uit elkaar lopen. */}
        <p className="text-xs text-[hsl(var(--muted-foreground))]"
           data-testid="taak-aangemaakt-deadline">{rij.deadline_op_glas}</p>
        <a href={rij.verwijzing} target="_blank" rel="noreferrer"
           className="text-xs underline text-[hsl(var(--accent))] inline-flex items-center gap-1 min-h-11 py-3"
           data-testid="taak-link">
          {rij.titel}
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-3 space-y-3" data-testid="taak-nalezen">
      {/* ALLEEN-LEZEN EN GEEN INVOERVELDEN. Wat hier staat komt van de server
          terug; het is de opgeslagen rij en niet die van dit scherm. */}
      <dl className="text-xs space-y-1">
        <div className="flex gap-2">
          <dt className="w-24 shrink-0 text-[hsl(var(--muted-foreground))]">{t("Werkruimte")}</dt>
          <dd className="min-w-0 font-medium" data-testid="taak-werkruimte">
            {rij.werkruimte_naam}
            {/* **TWEE SCHRIJFACTIES ONDER EEN KNOP, EN DAT HOORT OP GLAS.**
                Wie bevestigt maakt hier niet alleen een taak aan maar ook een
                werkruimte, en dat verschil hoort hij te zien voordat hij
                drukt. */}
            {rij.nieuwe_werkruimte && (
              <span className="ml-2 font-normal text-[hsl(var(--muted-foreground))]"
                    data-testid="taak-nieuwe-werkruimte">
                {t("bestaat nog niet, wordt aangemaakt")}
              </span>
            )}
          </dd>
        </div>
        <div className="flex gap-2">
          <dt className="w-24 shrink-0 text-[hsl(var(--muted-foreground))]">{t("Taak")}</dt>
          <dd className="min-w-0 font-medium" data-testid="taak-titel">{rij.titel}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="w-24 shrink-0 text-[hsl(var(--muted-foreground))]">{t("Deadline")}</dt>
          <dd className="min-w-0" data-testid="taak-deadline">{rij.deadline_op_glas}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="w-24 shrink-0 text-[hsl(var(--muted-foreground))]">{t("Toegewezen")}</dt>
          <dd className="min-w-0" data-testid="taak-toegewezen">{rij.toegewezen}</dd>
        </div>
      </dl>
      {/* **WAT ER IS GENEGEERD, IN EEN REGEL. Post 10b-fix punt 3.** Frank typte
          twee werkruimtes en er is er een gebruikt. Dat stil doen zou hem laten
          denken dat beide zijn begrepen, en bij de volgende zin dezelfde fout
          laten maken. Zelfde keuze als `onleesbaar` bij de deadline. */}
      {rij.genegeerd && (
        <p className="text-xs text-[hsl(var(--muted-foreground))]"
           data-testid="taak-genegeerd">{rij.genegeerd}</p>
      )}
      {rij.onleesbaar && (
        <p className="text-xs text-[hsl(var(--muted-foreground))]"
           data-testid="taak-onleesbaar">
          {t("\"{tekst}\" kon ik niet als datum lezen, dus deze taak heeft geen deadline.", { tekst: rij.onleesbaar })}
        </p>
      )}
      {(fout || reden) && (
        <p className="text-xs text-[hsl(var(--destructive))]" data-testid="taak-foutmelding">{fout || reden}</p>
      )}
      {/* **MINSTENS 44 PUNTEN HOOG, EN DAT IS GEMETEN EN NIET GESCHAT.**
          CONSTRAINTS 0.3b noemt alles onder 44 een DEFECT en geen detail. De
          eerste versie gebruikte `size="sm"` en kwam op 32 uit; achttien groene
          toetsen gaven daar geen signaal, want jsdom kent geen opmaak en kan
          die fout dus niet eens maken. `meting/taakkaart.mjs` vond hem meteen.

          DEZE KNOPPEN MAKEN DE TAAK ECHT AAN. Juist daar hoort geen krap
          tikdoel te zitten: mis je Wijzig en raak je Bevestig, dan staat er een
          taak in de lijst van iedereen die die werkruimte deelt. */}
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" className={`min-h-11 px-4 ${vervallen ? GRIJS : ""}`} onClick={bevestig} disabled={bezig || !token}
                data-testid="taak-bevestig-btn">
          {bezig ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                 : <Check className="h-3 w-3 mr-1" />}
          {t("Bevestig")}
        </Button>
        {vervallen && <OpenOpnieuw onClick={openOpnieuw} bezig={bezig} />}
        <Button size="sm" variant="outline" className="min-h-11 px-4" disabled={bezig}
                onClick={wijzig} data-testid="taak-wijzig-btn">
          <Pencil className="h-3 w-3 mr-1" />
          {t("Wijzig")}
        </Button>
      </div>
    </div>
  );
}
